import { Injectable, inject } from '@angular/core';
import { type Observable } from 'rxjs';
import {
  SbeSmsApiService,
  type CreateOriginatorBodyDto,
  type ListOriginatorsResponseDto,
  type OrgDocumentUploadUrlBodyDto,
  type OrgDocumentUploadUrlResponseDto,
  type OriginatorDocumentDownloadUrlDto,
  type RegisterOrgDocumentBodyDto,
  type RegisterOrgDocumentResponseDto,
  type OriginatorDto,
  type UpdateOriginatorBodyDto,
  type UploadUrlBodyDto,
  type UploadUrlResponseDto,
} from '@sbe-spinoff-client/sbe-sms-api';

/**
 * /originators facade. orval 의 controller-suffix 메서드 이름을 web 의미명으로
 * 1:1 wrap. orgId 를 넘기면 단체 컨텍스트 라우트(orgs/:orgId/originators, orval의
 * orgOriginatorsController*)로, 생략하면 개인 컨텍스트 라우트로 위임한다
 * (messages-api.service.ts 와 동일 패턴).
 *
 * org 라우트는 URL이 `/orgs/:orgId/...`이라 `spinoffAuthInterceptor`가
 * `resolveOrgScopedId`로 자동 인식해 org access token을 붙인다(2026-07-23).
 */
@Injectable({ providedIn: 'root' })
export class OriginatorsApiService {
  private readonly api = inject(SbeSmsApiService);

  list(
    params: {
      verifiedOnly?: boolean;
      limit?: number;
      includeDocuments?: boolean;
    } = {},
    orgId?: string,
  ): Observable<ListOriginatorsResponseDto> {
    const query = {
      ...(params.verifiedOnly !== undefined ? { verifiedOnly: params.verifiedOnly } : {}),
      ...(params.limit !== undefined ? { limit: params.limit } : {}),
      ...(params.includeDocuments !== undefined ? { includeDocuments: params.includeDocuments } : {}),
    };
    return orgId
      ? this.api.orgOriginatorsControllerList<ListOriginatorsResponseDto>(orgId, query)
      : this.api.originatorsControllerList<ListOriginatorsResponseDto>(query);
  }

  getById(id: string, orgId?: string): Observable<OriginatorDto> {
    return orgId
      ? this.api.orgOriginatorsControllerGetById<OriginatorDto>(orgId, id)
      : this.api.originatorsControllerGetById<OriginatorDto>(id);
  }

  getUploadUrl(body: UploadUrlBodyDto, orgId?: string): Observable<UploadUrlResponseDto> {
    return orgId
      ? this.api.orgOriginatorsControllerGetUploadUrl<UploadUrlResponseDto>(orgId, body)
      : this.api.originatorsControllerGetUploadUrl<UploadUrlResponseDto>(body);
  }

  getOrgDocumentUploadUrl(
    body: OrgDocumentUploadUrlBodyDto,
    orgId?: string,
  ): Observable<OrgDocumentUploadUrlResponseDto> {
    return orgId
      ? this.api.orgOriginatorsControllerGetOrgDocumentUploadUrl<OrgDocumentUploadUrlResponseDto>(orgId, body)
      : this.api.originatorsControllerGetOrgDocumentUploadUrl<OrgDocumentUploadUrlResponseDto>(body);
  }

  registerOrgDocument(
    body: RegisterOrgDocumentBodyDto,
    orgId?: string,
  ): Observable<RegisterOrgDocumentResponseDto> {
    return orgId
      ? this.api.orgOriginatorsControllerRegisterOrgDocument<RegisterOrgDocumentResponseDto>(orgId, body)
      : this.api.originatorsControllerRegisterOrgDocument<RegisterOrgDocumentResponseDto>(body);
  }

  create(body: CreateOriginatorBodyDto, orgId?: string): Observable<OriginatorDto> {
    return orgId
      ? this.api.orgOriginatorsControllerCreate<OriginatorDto>(orgId, body)
      : this.api.originatorsControllerCreate<OriginatorDto>(body);
  }

  update(id: string, body: UpdateOriginatorBodyDto, orgId?: string): Observable<OriginatorDto> {
    return orgId
      ? this.api.orgOriginatorsControllerUpdate<OriginatorDto>(orgId, id, body)
      : this.api.originatorsControllerUpdate<OriginatorDto>(id, body);
  }

  getDocumentDownloadUrl(
    originatorId: string,
    fileId: string,
    orgId?: string,
  ): Observable<OriginatorDocumentDownloadUrlDto> {
    return orgId
      ? this.api.orgOriginatorsControllerGetDocumentDownloadUrl<OriginatorDocumentDownloadUrlDto>(
          orgId,
          originatorId,
          fileId,
        )
      : this.api.originatorsControllerGetDocumentDownloadUrl<OriginatorDocumentDownloadUrlDto>(
          originatorId,
          fileId,
        );
  }

  remove(id: string, orgId?: string): Observable<void> {
    return orgId
      ? this.api.orgOriginatorsControllerRemove<void>(orgId, id)
      : this.api.originatorsControllerRemove<void>(id);
  }
}
